// eslint-disable-next-line import/no-cycle
import { defaultDoctorPhoto } from '@/store/doctor';

// Global variables
export const COLORS = {
  default: '',
  success: '#009688',
  failed: '',
  warning: '#fb8500',
};

// convert string date to DD/MM/YYYY
export const convertDateToDDMMYYYY = (dateString) => {
  const date = new Date(dateString);
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

// convert string date to MM/DD/YYYY
export const convertDateStringToMMDDYYYY = (date) => {
  const [day, month, year] = date.split('/');
  return new Date(`${month}/${day}/${year}`);
};

// makes start value starts at 00:00 in the morning
// makes end value ends at 23:59 at night
export const convertDateStringToISO = (date, time) => {
  const [day, month, year] = date.split('/');
  const [hours, mintues] = time.split(':');
  const dateObject = new Date(Date.UTC(year, month - 1, day));
  dateObject.setUTCHours(hours, mintues, 0, 0);

  return dateObject.toISOString();
};

export const isDaysBetweenOrLess = (startDateIso, endDateIso, between = 7) => {
  // Convert ISO strings to Date objects
  const startDate = new Date(startDateIso);
  const endDate = new Date(endDateIso);

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = endDate - startDate;

  // Convert the difference to days
  const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);

  // Check if the difference is less than 7 days
  return differenceInDays <= between;
};

export const convertDateToAgeString = (date) => {
  const birthDate = new Date(date);
  const today = new Date();

  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return `${years} year(s) ${months} month(s) ${days} day(s)`;
};

export const getProfilePhoto = (photo, gender) => {
  if (photo && photo !== defaultDoctorPhoto) return photo;
  if (!gender) return '/unknown-male.png';
  if (gender.toLowerCase() === 'female') return '/unknown-female.png';
  return '/unknown-male.png';
};

export const cleanFileUrl = (input, baseUrl) => {
  // Ensure input is a string
  if (typeof input !== 'string') {
    return input;
  }

  // Ensure baseUrl is provided, default to empty string if not
  const url = baseUrl || '';

  // Replace "http://localhost:1337" with the provided URL
  return input.replace(/http:\/\/localhost:1337\/parse/g, url);
};

// calculate Steps Mertics
export const getColorIsActive = (isActive) => {
  const color = 'green';
  return isActive ? color : 'red';
};

// Helper function to check if two time ranges overlap
const isOverlapping = (range1, range2) => new Date(range1.start) < new Date(range2.end)
  && new Date(range1.end) > new Date(range2.start);

export const calculateAverage = (field, object) => {
  // Ensure the field exists in the steps array
  if (!object.steps || !object.steps.length) {
    return 0;
  }

  // Sum the specified field
  const total = object.steps.reduce((sum, step) => sum + (step[field] || 0), 0);

  // Calculate the average
  const count = object.steps.length;
  return total / count;
};

export const calculateSum = (field, object) => {
  // Ensure the field exists in the steps array
  if (!object.steps || !object.steps.length) {
    return 0;
  }

  // Calculate the sum of the specified field
  return object.steps.reduce((total, step) => total + (step[field] || 0), 0);
};

export const calculateAverageWithTimeRange = (field, startTime, endTime, ...objects) => {
  // Combine all specified field values from all objects into a single array
  let allFields = [];
  objects.forEach((obj) => {
    allFields = [...allFields, ...obj.steps];
  });

  // Filter fields that fall within the provided start and end time range
  const filteredFields = allFields.filter((fieldObj) => {
    const fieldStart = new Date(fieldObj.start);
    const fieldEnd = new Date(fieldObj.end);
    return (
      fieldStart >= new Date(startTime)
      && fieldEnd <= new Date(endTime)
    );
  });

  // Sort the filtered fields based on start time to make comparison easier
  filteredFields.sort((a, b) => new Date(a.start) - new Date(b.start));

  // Array to store unique non-overlapping fields
  const uniqueFields = [];

  // Iterate through all the filtered fields
  filteredFields.forEach((currentField) => {
    let isDuplicate = false;

    // Check if the current field overlaps with any field in uniqueFields
    for (let i = 0; i < uniqueFields.length; i += 1) {
      if (isOverlapping(uniqueFields[i], currentField)) {
        isDuplicate = true;
        // If the overlapping field has a higher value, replace it
        if (currentField[field] > uniqueFields[i][field]) {
          uniqueFields[i] = currentField;
        }
        break;
      }
    }

    // If no overlap is found, add it to uniqueFields
    if (!isDuplicate) {
      uniqueFields.push(currentField);
    }
  });

  // Calculate the average of the specified field in uniqueFields
  const totalValue = uniqueFields.reduce((acc, entry) => acc + entry[field], 0);
  const averageValue = uniqueFields.length > 0 ? totalValue / uniqueFields.length : 0;

  return averageValue;
};

export const calculateMaxWithTimeRange = (field, startTime, endTime, ...objects) => {
  // Combine all specified field values from all objects into a single array
  let allFields = [];
  objects.forEach((obj) => {
    allFields = [...allFields, ...obj.steps];
  });

  // Filter fields that fall within the provided start and end time range
  const filteredFields = allFields.filter((fieldObj) => {
    const fieldStart = new Date(fieldObj.start);
    const fieldEnd = new Date(fieldObj.end);
    return (
      fieldStart >= new Date(startTime)
      && fieldEnd <= new Date(endTime)
    );
  });

  // Sort the filtered fields based on start time to make comparison easier
  filteredFields.sort((a, b) => new Date(a.start) - new Date(b.start));

  // Array to store unique non-overlapping fields
  const uniqueFields = [];

  // Iterate through all the filtered fields
  filteredFields.forEach((currentField) => {
    let isDuplicate = false;

    // Check if the current field overlaps with any field in uniqueFields
    for (let i = 0; i < uniqueFields.length; i += 1) {
      if (isOverlapping(uniqueFields[i], currentField)) {
        isDuplicate = true;
        // If the overlapping field has a higher value, replace it
        if (currentField[field] > uniqueFields[i][field]) {
          uniqueFields[i] = currentField;
        }
        break;
      }
    }

    // If no overlap is found, add it to uniqueFields
    if (!isDuplicate) {
      uniqueFields.push(currentField);
    }
  });

  // Find the maximum value of the specified field in uniqueFields
  const maxValue = uniqueFields.reduce((max, entry) => Math.max(max, entry[field]), -Infinity);

  return maxValue;
};

export const calculateSumWithTimeRange = (field, startTime, endTime, ...objects) => {
  // Combine all specified field values from all objects into a single array
  let allFields = [];
  objects.forEach((obj) => {
    allFields = [...allFields, ...obj.steps];
  });

  // Filter fields that fall within the provided start and end time range
  const filteredFields = allFields.filter((fieldObj) => {
    const fieldStart = new Date(fieldObj.start);
    const fieldEnd = new Date(fieldObj.end);
    return (
      fieldStart >= new Date(startTime)
      && fieldEnd <= new Date(endTime)
    );
  });

  // Sort the filtered fields based on start time to make comparison easier
  filteredFields.sort((a, b) => new Date(a.start) - new Date(b.start));

  // Array to store unique non-overlapping fields
  const uniqueFields = [];

  // Iterate through all the filtered fields
  filteredFields.forEach((currentField) => {
    let isDuplicate = false;

    // Check if the current field overlaps with any field in uniqueFields
    for (let i = 0; i < uniqueFields.length; i += 1) {
      if (isOverlapping(uniqueFields[i], currentField)) {
        isDuplicate = true;
        // If the overlapping field has more value, replace it
        if (currentField[field] > uniqueFields[i][field]) {
          uniqueFields[i] = currentField;
        }
        break;
      }
    }

    // If no overlap is found, add it to uniqueFields
    if (!isDuplicate) {
      uniqueFields.push(currentField);
    }
  });

  // Sum the values of the specified field in uniqueFields
  const totalValue = uniqueFields.reduce((acc, entry) => acc + entry[field], 0);

  return totalValue;
};

export const calculateMax = (devices, field) => {
  if (!devices.length) return 0; // Return 0 or some default value if devices is empty

  return Math.max(...devices.map((device) => device[field]));
};

// get start and end days with their time
export const getPeriodDates = (value) => {
  const today = new Date();
  const lastDay = new Date();

  let startDate;

  switch (value) {
    case 'last24h':
      startDate = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      break;
    case 'last48h':
      startDate = new Date(today.getTime() - 48 * 60 * 60 * 1000);
      break;
    case 'lastWeek':
      startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case 'lastMonth':
      startDate = new Date(today.setMonth(today.getMonth() - 1));
      break;
    case 'last3Months':
      startDate = new Date(today.setMonth(today.getMonth() - 3));
      break;
    case 'last6Months':
      startDate = new Date(today.setMonth(today.getMonth() - 6));
      break;
    case 'lastYear':
      startDate = new Date(today.setFullYear(today.getFullYear() - 1));
      break;
    default:
      startDate = today;
      break;
  }

  const start = {
    day: convertDateToDDMMYYYY(startDate),
    time: `${startDate.getHours()}:${startDate.getMinutes()}`,
  };
  const end = {
    day: convertDateToDDMMYYYY(lastDay),
    time: `${lastDay.getHours()}:${lastDay.getMinutes()}`,
  };

  return {
    start,
    end,
  };
};

const getArrayDaysBetween = (start, end) => {
  const dates = [];
  const currentDate = new Date(start);
  while (currentDate <= end) {
    dates.push({
      timestamp: currentDate.getTime(),
      day: convertDateToDDMMYYYY(currentDate),
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

export const getArraTimeBetween = (startDate, endDate) => {
  const daysArray = [];

  // Calculate the total difference in hours
  const diffInHours = Math.floor((endDate - startDate) / (1000 * 60 * 60));

  // Loop for the total number of hours between start and end
  for (let i = 0; i <= diffInHours - 1; i += 1) {
    const currentDate = new Date(startDate.getTime() + i * 60 * 60 * 1000);

    const hours = String(currentDate.getHours()).padStart(2, '0');

    // Always set minutes to '00'
    const minutes = '00';

    daysArray.push({
      timestamp: currentDate.getTime(),
      day: convertDateToDDMMYYYY(currentDate),
      time: `${hours}:${minutes}`,
    });
  }

  return daysArray;
};

export const getHoursBetween = (startDate, endDate) => {
  // Calculate the difference in milliseconds
  const diffInMilliseconds = endDate - startDate;

  // Convert milliseconds to hours (1 hour = 60 * 60 * 1000 milliseconds)
  const hours = diffInMilliseconds / (1000 * 60 * 60);

  return hours;
};

// charts
const getHexColorByPosition = (position) => {
  // Generate a random number between 0 and 0xFFFFFF (decimal 16777215)
  const red = '#f94144';
  const green = '#00afb9';

  if (position === 'right') return green;
  return red;
};

const findHourIndex = (array, isoDate) => {
  // Parse the provided ISO date and get its timestamp
  const dateToFind = new Date(isoDate);
  const timestampToFind = dateToFind.getTime();

  // Loop through the array to find the index where the timestamp matches the required range
  for (let i = 0; i < array.length; i += 1) {
    const currentItem = array[i];
    const currentDay = currentItem.day; // "13/10/2024"
    const currentTime = currentItem.time; // "11:00"

    // Convert the day and time into a Date object
    const [day, month, year] = currentDay.split('/');
    const [hours, minutes] = currentTime.split(':');
    const currentDate = new Date(`${year}-${month}-${day}T${hours}:${minutes}:00.000Z`);

    // Calculate the start and end range for the hour
    const startOfHour = currentDate.getTime();
    const endOfHour = startOfHour + 59 * 60 * 1000 + 999;
    // Check if the given timestamp falls between the start and end of the hour
    if (timestampToFind >= startOfHour && timestampToFind <= endOfHour) {
      return i; // Return the index if found
    }
  }

  // Return -1 if no matching time is found
  return -1;
};

const findIndexByDay = (array, isoDate) => {
  // Parse the provided ISO date and extract the day in DD/MM/YYYY format
  const dateToFind = new Date(isoDate);
  const dayToFind = dateToFind.getDate().toString().padStart(2, '0'); // Get day with leading zero
  const monthToFind = (dateToFind.getMonth() + 1).toString().padStart(2, '0'); // Get month with leading zero
  const yearToFind = dateToFind.getFullYear();
  const formattedDayToFind = `${dayToFind}/${monthToFind}/${yearToFind}`; // Format as DD/MM/YYYY

  // Loop through the array to find the index where the day matches
  for (let i = 0; i < array.length; i += 1) {
    const currentDay = array[i].day; // "14/10/2024"

    if (currentDay === formattedDayToFind) {
      return i; // Return the index if day matches
    }
  }

  // Return -1 if no matching day is found
  return -1;
};

export const updateChartDataByDay = (devices, startIso, endIso, field = 'steps') => {
  // dates
  const data = [];
  const datasets = [];

  const averagesField = ['avgStepTime', 'cadence'];

  // Find the min and max dates
  const startDate = new Date(startIso);
  const endDate = new Date(endIso);

  const days = getArrayDaysBetween(startDate, endDate);
  const labels = days.map(({ day }) => day);

  devices.forEach((device, index) => {
    device.steps.forEach((step) => {
      // Find its index in labels
      const labelIndex = findIndexByDay(days, step.start);

      if (data[index] === undefined || data[index] === null) {
        data[index] = [];
      }

      // check if there more steps in the same day
      if (data[index][labelIndex] > 0) {
        if (averagesField.includes(field)) {
          const isoDate = step.start.split('T')[0];
          const startDay = `${isoDate}T00:00:01.000Z`;
          const endDay = `${isoDate}T23:59:59.000Z`;

          data[index][labelIndex] = calculateMaxWithTimeRange(field, startDay, endDay, device);
        } else {
          data[index][labelIndex] += step[field];
        }
      } else {
        data[index][labelIndex] = step[field];
      }
    });

    datasets.push({
      label: device.position,
      backgroundColor: getHexColorByPosition(device.position),
      data: data[index],
    });
  });

  return {
    labels,
    datasets,
  };
};

export const updateChartDataByHour = (devices, startIso, endIso, field) => {
  // dates
  const data = [];
  const datasets = [];

  const averagesField = ['avgStepTime', 'cadence'];

  // Find the min and max dates
  const startDate = new Date(startIso);
  const endDate = new Date(endIso);

  const days = getArraTimeBetween(startDate, endDate);
  const labels = days.map((day) => day.time);

  devices.forEach((device, index) => {
    device.steps.forEach((step) => {
      // Find its index in labels
      const labelIndex = findHourIndex(days, step.start);

      if (data[index] === undefined || data[index] === null || data[index] === -1) {
        data[index] = [];
      }

      // check if there more steps in the same day
      if (data[index][labelIndex] > 0) {
        if (averagesField.includes(field)) {
          const startDay = step.start;
          const endDay = step.end;
          data[index][labelIndex] = calculateMaxWithTimeRange(field, startDay, endDay, device);
        } else {
          data[index][labelIndex] += step[field];
        }
      } else {
        data[index][labelIndex] = step[field];
      }
    });

    datasets.push({
      label: device.position,
      backgroundColor: getHexColorByPosition(device.position),
      data: data[index],
    });
  });

  return {
    labels,
    datasets,
  };
};

export const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);
